"use client";

import { X } from "lucide-react";
import { useRef } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

type BulkActionsToolbarProps = {
  selectedCount: number;
  entityName: string;
  onClear: () => void;
  children: React.ReactNode;
};

/**
 * A reusable bulk actions toolbar for any selection-based UI.
 *
 * @param {number} selectedCount Number of selected items.
 * @param {string} entityName Name of the entity (e.g., "user", "task").
 * @param {() => void} onClear Function to clear the selection.
 * @param {React.ReactNode} children Action buttons.
 */
export function BulkActionsToolbar({
  selectedCount,
  entityName,
  onClear,
  children,
}: BulkActionsToolbarProps) {
  const toolbarRef = useRef<HTMLDivElement>(null);
  const announcement =
    selectedCount > 0
      ? `${selectedCount} ${entityName}${
          selectedCount > 1 ? "s" : ""
        } selected. Bulk actions toolbar is available.`
      : "";

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const buttons = toolbarRef.current?.querySelectorAll("button");
    if (!buttons) return;

    const currentIndex = Array.from(buttons).findIndex(
      (button) => button === document.activeElement,
    );

    switch (event.key) {
      case "ArrowRight": {
        event.preventDefault();
        const nextIndex = (currentIndex + 1) % buttons.length;
        buttons[nextIndex]?.focus();
        break;
      }
      case "ArrowLeft": {
        event.preventDefault();
        const prevIndex =
          currentIndex === 0 ? buttons.length - 1 : currentIndex - 1;
        buttons[prevIndex]?.focus();
        break;
      }
      case "Home":
        event.preventDefault();
        buttons[0]?.focus();
        break;
      case "End":
        event.preventDefault();
        buttons[buttons.length - 1]?.focus();
        break;
      case "Escape":
        event.preventDefault();
        onClear();
        break;
    }
  };

  if (selectedCount === 0) return null;

  return (
    <>
      {/* Live region for screen reader announcements */}
      <div
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
        role="status"
      >
        {announcement}
      </div>

      <div
        ref={toolbarRef}
        role="toolbar"
        aria-label={`Bulk actions for ${selectedCount} selected ${entityName}${
          selectedCount > 1 ? "s" : ""
        }`}
        aria-describedby="bulk-actions-description"
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        className={cn(
          "fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-xl",
          "",
          "focus-visible:ring-ring/50 focus-visible:ring-2 focus-visible:outline-none",
        )}
      >
        <div
          className={cn(
            "py-1.5 px-2 shadow-xl",
            "rounded-xl border",
            " bg-background",
            "flex items-center gap-x-1",
          )}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                onClick={onClear}
                className="size-6 rounded-full"
                aria-label="Clear selection"
                title="Clear selection (Escape)"
              >
                <X />
                <span className="sr-only">Clear selection</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Clear selection (Escape)</p>
            </TooltipContent>
          </Tooltip>

          <Separator
            className="h-5"
            orientation="vertical"
            aria-hidden="true"
          />

          <div
            className="flex items-center gap-x-1 text-sm"
            id="bulk-actions-description"
          >
            <Badge
              variant="default"
              className="min-w-8 rounded-lg"
              aria-label={`${selectedCount} selected`}
            >
              {selectedCount}
            </Badge>{" "}
            {/* <span className="hidden sm:inline">
              {entityName}
              {selectedCount > 1 ? "s" : ""}
            </span>{" "} */}
            selected
          </div>

          <Separator
            className="h-5"
            orientation="vertical"
            aria-hidden="true"
          />

          {children}
        </div>
      </div>
    </>
  );
}
